/**
 * Shared, bounded caption measurement. Read existing geometry first, batch any
 * expanded-caption probe writes, then read all probes before removing them.
 * Collapsed captions need no DOM writes at all.
 */
type Measurement = {
  element: HTMLParagraphElement;
  lineClamp: number;
  expanded: boolean;
  onMeasure: (overflowing: boolean) => void;
};

const pending = new Map<object, Measurement>();
const BATCH_SIZE = 8;
let frame: number | undefined;

function flush() {
  frame = undefined;
  const batch = Array.from(pending.entries()).slice(0, BATCH_SIZE);
  batch.forEach(([key]) => pending.delete(key));
  const prepared = batch
    .filter(([, job]) => job.element.isConnected)
    .map(([, job]) => ({ job, width: job.element.clientWidth }))
    .filter(({ width }) => width > 0);
  const probes = prepared.map(({ job, width }) => {
    if (!job.expanded) return { job, probe: job.element, cloned: false };
    const probe = job.element.cloneNode(true) as HTMLParagraphElement;
    probe.removeAttribute("id");
    probe.querySelectorAll("[id]").forEach((child) => child.removeAttribute("id"));
    probe.setAttribute("aria-hidden", "true");
    probe.setAttribute("inert", "");
    Object.assign(probe.style, {
      position: "absolute",
      visibility: "hidden",
      pointerEvents: "none",
      left: "0",
      top: "0",
      width: `${width}px`,
      display: "-webkit-box",
      webkitLineClamp: String(job.lineClamp),
      webkitBoxOrient: "vertical",
      overflow: "hidden",
    });
    job.element.parentElement?.appendChild(probe);
    return { job, probe, cloned: true };
  });
  const results = probes.map(({ probe }) => probe.scrollHeight > probe.clientHeight + 1);
  probes.forEach(({ probe, cloned }) => { if (cloned) probe.remove(); });
  probes.forEach(({ job }, index) => job.onMeasure(results[index]));
  if (pending.size && frame === undefined) frame = requestAnimationFrame(flush);
}

export function scheduleCaptionMeasurement(key: object, job: Measurement) {
  pending.set(key, job);
  if (frame === undefined) frame = requestAnimationFrame(flush);
}

export function cancelCaptionMeasurement(key: object) {
  pending.delete(key);
  if (!pending.size && frame !== undefined) {
    cancelAnimationFrame(frame);
    frame = undefined;
  }
}